import ApiService from '@/core/services/ApiService';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import { Module, Action, Mutation, VuexModule } from 'vuex-module-decorators';

export interface UserObject {
  id: number;
  mobile: number;
  officeId: string;
  clientId: string;
  clinetId: string;
  roleId: string;
  fullname: string;
  email: string;
  type: string;
  isActive: boolean;
  title: string;
  offices: string;
}

export interface MerchantObject {
  id: string;
  name: string;
  email: string;
  isDone: boolean;
}

export interface UsersListInfo {
  page: number;
  take: number;
  itemCount: number;
  pageCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  users: Array<UserObject>;
  merchant: Array<MerchantObject>;
  errors: unknown;
}

@Module
export default class UsersModule extends VuexModule implements UsersListInfo {
  users = [] as Array<UserObject>;
  merchant = [] as Array<MerchantObject>;
  page = 1;
  take = 10;
  itemCount = 0;
  pageCount = 1;
  hasPreviousPage = false;
  hasNextPage = true;
  errors = {};

  /**
   * Get users list
   * @returns Array<UserObject>
   */
  get usersList(): Array<UserObject> {
    return this.users;
  }

  get merchantList(): Array<MerchantObject> {
    return this.merchant;
  }

  /**
   * Get total items
   * @returns number
   */
  get usersListCount(): number {
    return this.itemCount;
  }

  get merchantListCount(): number {
    return this.itemCount;
  }

  @Mutation
  [Mutations.SET_USERS](data) {
    this.users = data.data;
    this.page = data.meta.page;
    this.take = data.meta.take;
    this.itemCount = data.meta.itemCount;
    this.pageCount = data.meta.pageCount;
    this.hasPreviousPage = data.meta.hasPreviousPage;
    this.hasNextPage = data.meta.hasNextPage;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_MERCHANT](data) {
    this.merchant = data.data;
    this.page = data.meta.page;
    this.take = data.meta.take;
    this.itemCount = data.meta.itemCount;
    this.pageCount = data.meta.pageCount;
    this.hasPreviousPage = data.meta.hasPreviousPage;
    this.hasNextPage = data.meta.hasNextPage;
    this.errors = {};
  }

  @Mutation
  [Mutations.RESET_MERCHANT_LIST]() {
    this.merchant = [] as Array<MerchantObject>;
    this.page = 1;
    this.take = 10;
    this.itemCount = 0;
    this.pageCount = 1;
    this.hasPreviousPage = false;
    this.hasNextPage = true;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_USERS_CURRENT_PAGE](page) {
    this.page = page;
  }

  @Mutation
  [Mutations.SET_MERCHANT_CURRENT_PAGE](page) {
    this.page = page;
  }

  @Mutation
  [Mutations.RESET_USERS_LIST]() {
    this.users = [] as Array<UserObject>;
    this.page = 1;
    this.take = 10;
    this.itemCount = 0;
    this.pageCount = 1;
    this.hasPreviousPage = false;
    this.hasNextPage = true;
    this.errors = {};
  }

  @Action
  [Actions.RESET_USERS_STORE]() {
    this.context.commit(Mutations.RESET_USERS_LIST);
  }

  @Action
  [Actions.CREATE_CLIENT_USER](data) {
    ApiService.setAuthorizationHeader();
    return ApiService.post('/client-users', data)
      .then(() => {
        this.context.commit(Mutations.RESET_USERS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.DELETE_USER](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.delete('/users', id)
      .then(() => {
        this.context.commit(Mutations.RESET_USERS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.UPDATE_USER]({ id, data }) {
    ApiService.setAuthorizationHeader();
    return ApiService.update('/users', id, data)
      .then(() => {
        this.context.commit(Mutations.RESET_USERS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.UPDATE_USER_CURRENT_PAGE](val) {
    this.context.commit(Mutations.SET_USERS_CURRENT_PAGE, val);
  }

  @Action
  [Actions.UPDATE_MERCHANT_CURRENT_PAGE](val) {
    this.context.commit(Mutations.SET_MERCHANT_CURRENT_PAGE, val);
  }

  @Action
  [Actions.GET_USER](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/users', id);
  }

  @Action
  [Actions.GET_USERS](input) {
    const params = {
      page: typeof input === 'string' ? 1 : this.page,
      fullName: input,
    };
    ApiService.setAuthorizationHeader();
    return ApiService.query('/users', { params })
      .then(({ data }) => {
        this.context.commit(Mutations.SET_USERS, data);
      })
      .catch(({ response }) => {
        console.error(response);
        // this.context.commit(Mutations.SET_ERROR, {
        //   [response.statusText]: [response.data.statusText],
        // });
      });
  }

  @Action
  [Actions.GET_MERCHANT]() {
    ApiService.setAuthorizationHeader();
    return ApiService.query('/users/deleteRequests', {
      params: { page: this.page },
    })
      .then(({ data }) => {
        this.context.commit(Mutations.SET_MERCHANT, data);
      })
      .catch(({ response }) => {
        console.error(response);
        // this.context.commit(Mutations.SET_ERROR, {
        //   [response.statusText]: [response.data.statusText],
        // });
      });
  }

  @Action
  [Actions.DELETE_MERCHANT](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.delete('/users/deleteRequestAccount', id)
      .then(() => {
        this.context.commit(Mutations.RESET_MERCHANT_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }
}
